<div class="ps-page">
  <div class="ps-header">
    <a class="ps-back" routerLink="/projects">
      <ps-outline-icon name="chevron-left"></ps-outline-icon>
      Back to Projects
    </a>

    <div class="ps-header-row">
      <div class="ps-header-block ps-header-block--title">
        <h1 class="ps-title">Project Summary</h1>
      </div>

      <div class="ps-header-divider" *ngIf="project?.ProPakProject?.Asset?.Name"></div>

      <div class="ps-header-block" *ngIf="project?.ProPakProject?.Asset?.Name">
        <div class="ps-meta-item">
          <span class="ps-meta-icon">
            <ps-outline-icon name="building"></ps-outline-icon>
          </span>
          <div>
            <div class="ps-meta-label">Asset</div>
            <div class="ps-meta-value">{{ project.ProPakProject.Asset.Name }}</div>
          </div>
        </div>
      </div>

      <div class="ps-header-divider" *ngIf="project?.ProPakProject?.Name"></div>

      <div class="ps-header-block" *ngIf="project?.ProPakProject?.Name">
        <div class="ps-meta-item">
          <span class="ps-meta-icon">
            <ps-outline-icon name="cube"></ps-outline-icon>
          </span>
          <div>
            <div class="ps-meta-label">Project</div>
            <div class="ps-meta-value is-project">
              <a *ngIf="project.MasterReport; else plainName" [href]="project.MasterReport" target="_blank"
                title="Go to Report">{{ project.ProPakProject.Name }}</a>
              <ng-template #plainName>{{ project.ProPakProject.Name }}</ng-template>
            </div>
          </div>
        </div>
      </div>

      <div class="ps-header-actions" *ngIf="project?.ProPakProject?.ProjectId">
        <button type="button" class="ps-star"
          (click)="toggleFavorite()"
          [class.is-on]="isFavorited"
          [title]="isFavorited ? 'Remove from favorites' : 'Add to favorites'">
          <fa-icon [icon]="faStar" [class.is-favorited]="isFavorited"></fa-icon>
        </button>

        <div class="ps-edit">
          <a class="ps-edit-main" [routerLink]="['/edit-project', project.ProPakProject.ProjectId]">
            <ps-outline-icon name="pencil"></ps-outline-icon>
            Edit
          </a>
          <button type="button" class="ps-edit-caret" (click)="toggleEditMenu($event)" title="More actions">
            <ps-outline-icon name="chevron-down"></ps-outline-icon>
          </button>
          <div class="ps-edit-menu" *ngIf="editMenuOpen">
            <button type="button" (click)="openProjectInProPak($event)">Open Project in ProPak</button>
            <button type="button" (click)="openDetailedReporting($event)">Open Detailed Reporting</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Remount insights panel when projectId changes so prior project text cannot linger in the DOM -->
  <section class="ps-insights" *ngIf="projectId" [attr.data-project-id]="projectId">
    <div class="ps-insights__header">
      <div>
        <h2 class="ps-insights__title">AI Insights</h2>
        <p class="ps-insights__sub">On-demand progress analysis from readiness, materials, execution, and commissioning data</p>
      </div>
      <button type="button"
              class="ps-insights__btn"
              (click)="generateAiInsights()"
              [disabled]="insightsLoading">
        <fa-icon [icon]="insightsLoading ? faSpinner : faMagic" [spin]="insightsLoading"></fa-icon>
        {{ insightsLoading ? 'Generating…' : (hasVisibleInsights ? 'Regenerate Insights' : 'Generate AI Insights') }}
      </button>
    </div>
    <ng-container *ngIf="hasVisibleInsights; else insightsIdle">
      <div class="ps-insights__body">
        <pre class="ps-insights__text">{{ insightsText }}</pre>
        <div class="ps-insights__meta" *ngIf="insightsGeneratedAt">
          Generated {{ insightsGeneratedAt | date:'medium' }}
        </div>
      </div>
    </ng-container>
    <ng-template #insightsIdle>
      <div class="ps-insights__body" *ngIf="insightsLoading">
        <p class="ps-insights__placeholder">Analyzing project data…</p>
      </div>
    </ng-template>
  </section>

  <div class="ps-body">
    <div class="slider-container">
      <div class="slider-content" [style.transform]="getTransform()">
        <div class="slide" *ngIf="showReadinessColumn" [class.is-visible]="isColumnVisible('readiness')">
          <ng-container *ngIf="readinessSummary; else readinessLoading">
            <project-summary-readiness [readinessSummary]="readinessSummary"
              [proPakProjectId]="projectId"
              [isActive]="isColumnVisible('readiness')"
              [playToken]="currentSlideIndex"></project-summary-readiness>
          </ng-container>
        </div>

        <div class="slide" *ngIf="showMaterialsColumn" [class.is-visible]="isColumnVisible('materials')">
          <ng-container *ngIf="materialsSummary; else materialsLoading">
            <project-summary-materials [materialsSummary]="materialsSummary"
              [isActive]="isColumnVisible('materials')"
              [playToken]="currentSlideIndex"></project-summary-materials>
          </ng-container>
        </div>

        <div class="slide" *ngIf="showExecutionColumn" [class.is-visible]="isColumnVisible('execution')">
          <ng-container *ngIf="executionSummary; else executionLoading">
            <project-summary-execution [executionSummary]="executionSummary" [chartData]="executionChartData"
              [barChartData]="executionBarChartData" [lineChartData]="executionLineChartData" [projectId]="projectId"
              [powerBiReportPath]="powerBiReportPath" [projectDisplayType]="projectDisplayType"
              [project]="project"
              [isActive]="isColumnVisible('execution')"
              [playToken]="currentSlideIndex"></project-summary-execution>
          </ng-container>
        </div>

        <div class="slide" *ngIf="showCommissioningColumn" [class.is-visible]="isColumnVisible('commissioning')">
          <ng-container *ngIf="cxPlannerSummary; else cxPlannerLoading">
            <project-cxplanner-summary [cxPlannerSummary]="cxPlannerSummaryStage"
              [isActive]="isColumnVisible('commissioning')"
              [playToken]="currentSlideIndex"></project-cxplanner-summary>
          </ng-container>
        </div>
      </div>
    </div>

    <button type="button" class="ps-nav left" (click)="moveLeft()" *ngIf="canMoveLeft" aria-label="Previous column">
      <ps-outline-icon name="chevron-left"></ps-outline-icon>
    </button>
    <button type="button" class="ps-nav right" (click)="moveRight()" *ngIf="canMoveRight" aria-label="Next column">
      <ps-outline-icon name="chevron-right"></ps-outline-icon>
    </button>
  </div>

  <div class="ps-footer">
    <!-- <span>All dates shown in local project time zone • Data as of {{ dataAsOfLabel }}</span> -->
    <button type="button" class="ps-refresh" (click)="refreshData()" [disabled]="refreshing">
      <ps-outline-icon name="refresh" [class.spin]="refreshing"></ps-outline-icon>
      Refresh Data
    </button>
  </div>
</div>

<ng-template #readinessLoading>
  <div class="ps-card placeholder-card">
    <div class="placeholder-title shimmer"></div>
    <div class="placeholder-content shimmer"></div>
  </div>
</ng-template>
<ng-template #materialsLoading>
  <div class="ps-card placeholder-card">
    <div class="placeholder-title shimmer"></div>
    <div class="placeholder-content shimmer"></div>
  </div>
</ng-template>
<ng-template #executionLoading>
  <div class="ps-card placeholder-card">
    <div class="placeholder-title shimmer"></div>
    <div class="placeholder-content shimmer"></div>
  </div>
</ng-template>
<ng-template #cxPlannerLoading>
  <div class="ps-card placeholder-card">
    <div class="placeholder-title shimmer"></div>
    <div class="placeholder-content shimmer"></div>
  </div>
</ng-template>
